import type { Game } from 'chessboard/model/game/types';
import type JCE from '@chesscom/js-chess-engine';
import type { ThemeAssets } from 'chessboard/config/theme-assets';

export namespace Board {
  export class StylesSetFailed extends Error {}

  export namespace Animation {
    export enum Types {
      Arcade = 'arcade',
      Default = 'default',
      Fast = 'fast',
      Natural = 'natural',
      None = 'none',
      Slow = 'slow',
    }

    export enum Speeds {
      Default = 180,
      Fast = 100,
      Slow = 500,
    }
  }

  export interface Coords {
    x: number;
    y: number;
  }

  export enum HighlightColors {
    Selected = '#ffff00',
    Blue = '#52b0dc',
    Green = '#acce59',
    Orange = '#ffaa00',
    Red = '#eb6150',
  }

  export enum HighlightTypes {
    Analysis = 'analysis',
    Move = 'move',
    Selected = 'selected',
  }

  export enum HighlightedArrowColors {
    Blue = 'rgba(72, 193, 249, 0.8)',
    Green = 'rgba(159, 207, 63, 0.8)',
    Orange = 'rgba(255, 170, 0, 0.8)',
    Red = 'rgba(248, 85, 63, 0.8)',
  }

  export enum CoordinatesPositions {
    Inside = 'inside',
    Off = 'off',
    Outside = 'outside',
  }

  export interface Element extends HTMLElement {
    parentNode: HTMLElement;
  }

  export interface FileRank {
    file: number;
    rank: number;
  }

  export type GetPointerPosition = ({
    el,
    event,
    flipped,
  }: {
    el: HTMLElement;
    event: Event;
    flipped: boolean;
  }) => { coords: Coords; square: SanSquare | null } | null;

  export enum MoveMethods {
    Click = 'click',
    Drag = 'drag',
    Drag_Only = 'drag_only',
    /**
     * @deprecated Use `Drag_Only` instead. To be removed after settings service migration.
     */
    DragOnly = 'drag-only',
  }

  export enum MoveListDisplayTypes {
    Figurine = 'figurine',
    Text = 'text',
  }

  export enum MoveListClassificationStyles {
    Default = 'default',
    Traditional = 'traditional',
  }

  export interface PointerPosition {
    coords: Coords;
    coordsInsideBoard: Coords;
    coordsInsideSquare: Coords;
    square: SanSquare | null;
    squareInsideBoard: SanSquare | null;
  }

  export interface PromotionWindow {
    color: Game.Color;
    position: 'top' | 'bottom';
    file: number;
  }

  export interface ArrowColors {
    [Game.Node.Marking.JCEColorsMap.a]: string;
    [Game.Node.Marking.JCEColorsMap.d]: string;
    [Game.Node.Marking.JCEColorsMap.s]: string;
    [Game.Node.Marking.JCEColorsMap.l]: string;
  }

  export interface AnalysisHighlightColors {
    alt: string;
    ctrl: string;
    default: string;
    shift: string;
  }

  export interface Options<T = ThemeAssets.Assets> {
    allowMarkings: boolean;
    disableUserMarkings: boolean;
    analysisHighlightColors: AnalysisHighlightColors;
    analysisHighlightOpacity: number;
    animationType: Animation.Types;
    arrowColors: ArrowColors;
    aspectRatio: number;
    autoClaimDraw: boolean;
    autoPromote: boolean;
    autoResize: boolean;
    boardStyle: string;
    captureKeyStrokes: boolean;
    checkBlinkingSquareColor: string;
    coordinates: CoordinatesPositions;
    darkMode: boolean;
    diagramStyle: boolean;
    disableUserNavigation?: boolean;
    enabled: boolean;
    id?: string;
    isSiblingBoard?: boolean;
    fadeSetup: number;
    flipped: boolean;
    highlightColor?: string;
    highlightLegalMoves: boolean;
    highlightMoves: boolean;
    highlightOpacity: number;
    hoverSquareOutline: boolean;
    moveListContextMenuEnabled: boolean;
    moveListDisplayType: MoveListDisplayTypes;
    moveListClassificationStyle?: MoveListClassificationStyles;
    moveMethod: MoveMethods;
    overlayInAnalysisMode: boolean;
    pieceStyle: string;
    playSounds: boolean;
    premoveDelay: number;
    premoveHighlightColor: string;
    premoveHighlightOpacity: number;
    rounded: boolean;
    setupImprovements?: boolean;
    soundTheme: string;
    syncEffects: boolean;
    test?: boolean;
    themeAssets?: T;
    threatSquareColor: string;
    threatSquareOpacity: number;
    useSharedStyleTag: boolean;
    disableRookCastling?: boolean;
    assetPaths?: AssetPaths;
  }

  export interface AssetPaths {
    basePath?: string;
    enPassant?: string;
    soundsTheme?: string;
  }

  export enum BooleanOptions {
    HoverSquareOutline = 'hoverSquareOutline',
  }

  export enum SaveableOptions {
    AnimationType = 'animationType',
    BoardStyle = 'boardStyle',
    Coordinates = 'coordinates',
    DarkMode = 'darkMode',
    EvaluationBarType = 'evaluationBarType',
    HighlightLegalMoves = 'highlightLegalMoves',
    HighlightMoves = 'highlightMoves',
    MoveListDisplayType = 'moveListDisplayType',
    MoveListClassificationStyle = 'moveListClassificationStyle',
    PieceStyle = 'pieceStyle',
    SoundTheme = 'soundTheme',
    PlaySounds = 'playSounds',
  }

  export type SanSquare = JCE.SanSquare;
}
